/**
 * AdminPanel - Professional Admin Dashboard
 * Manages admin sections with permission-based access and modern UI
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Shield,
  CheckCircle2,
  GripVertical,
  Check,
  AlertCircle,
  Music,
  PoundSterling,
  Mail,
  MessageCircle,
  MonitorPlay,
  Building2,
  DoorOpen,
  GraduationCap,
  Wheat,
  LayoutDashboard,
  Activity,
  Bell,
  ChevronRight,
  Search,
  Settings,
  LogOut,
  User,
  Users,
  FileText,
  RefreshCw,
  BookOpen,
  Calendar,
  UserCheck
} from 'lucide-react';

// Import admin section components
import {
  FinanceSection,
  HarvestSection,
  ChoirMinistrySection,
  EmailComposeSection,
  MediaDepartmentSection,
  SecretarialAdminSection,
  MembersSection,
  SettingsSection,
  UsherDepartmentSection,
  SundaySchoolDepartmentSection,
  ShepherdCornerSection,
  ServiceScheduleSection,
  ManualAttendanceEntry,
  AnalyticsDashboard
} from './admin';
import GPSAttendancePage from '../GPSAttendancePage';
import RulesTenantsModule from './admin/RulesTenantsModule';
import BibleQuotesModule from './admin/BibleQuotesModule';
import RotaManagementModule from './admin/RotaManagementModule';
import AnnouncementsModule from './admin/AnnouncementsModule';
import BibleLessonManagement from './admin/BibleLessonManagement';
import VolunteerSection from './admin/VolunteerSection';

// Import RBAC utilities
import { 
  hasPermission as checkPermission, 
  canAccessDepartment,
  PERMISSIONS,
  DEPARTMENT_PERMISSIONS 
} from '../utils/rbac';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

// Notification Badge Component
const NotificationBadge = ({ count, size = 'sm' }) => {
  if (!count || count <= 0) return null;
  
  const sizeClasses = {
    sm: 'w-5 h-5 text-[10px]',
    md: 'w-6 h-6 text-xs',
    lg: 'w-7 h-7 text-sm'
  };
  
  return (
    <motion.span
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      className={`absolute -top-1 -right-1 ${sizeClasses[size]} bg-red-500 text-white rounded-full flex items-center justify-center font-bold shadow-lg`}
    >
      {count > 99 ? '99+' : count}
    </motion.span>
  );
};

// Professional sidebar navigation component with badges
const AdminSidebar = ({ sections, activeSection, setActiveSection, reorderMode, isMainAdmin, onMoveSection, notifications }) => {
  // Map section IDs to notification keys
  const getSectionBadge = (sectionId) => {
    if (!notifications) return 0;
    switch (sectionId) {
      case 'finance': return notifications.pending_finance_requests || 0;
      case 'choir': return notifications.pending_choir_applications || 0;
      case 'secretarial': return (notifications.unread_messages || 0) + (notifications.pending_enrollments || 0);
      default: return 0;
    }
  };

  return (
    <div className="hidden lg:block w-64 flex-shrink-0">
      <div className="bg-gradient-to-b from-violet-950 via-violet-900 to-violet-950 rounded-2xl p-4 sticky top-4 shadow-2xl border border-amber-400/20">
        <div className="space-y-1">
          {sections.map((section, index) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            const badgeCount = getSectionBadge(section.id);
            
            return (
              <motion.button
                key={section.id}
                onClick={() => !reorderMode && setActiveSection(section.id)}
                draggable={reorderMode && isMainAdmin}
                onDragStart={(e) => e.dataTransfer.setData('text/plain', index.toString())}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                  onMoveSection(fromIndex, index);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 group relative ${
                  isActive
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-violet-950 shadow-lg shadow-amber-500/30 font-bold'
                    : 'text-amber-100/70 hover:text-white hover:bg-violet-800/50'
                } ${reorderMode ? 'border-2 border-dashed border-amber-400/50 cursor-grab' : ''}`}
                whileHover={{ x: isActive ? 0 : 4 }}
                whileTap={{ scale: 0.98 }}
              >
                {reorderMode && <GripVertical className="w-4 h-4 text-amber-400" />}
                <div className={`relative p-2 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-violet-950/20' 
                    : 'bg-violet-900/50 group-hover:bg-violet-800'
                }`}>
                  <Icon className="w-4 h-4" />
                  {badgeCount > 0 && <NotificationBadge count={badgeCount} />}
                </div>
                <span className="font-medium text-sm flex-1">{section.label}</span>
                {isActive && (
                  <ChevronRight className="w-4 h-4" />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Mobile navigation tabs with badges
const MobileNavTabs = ({ sections, activeSection, setActiveSection, reorderMode, isMainAdmin, onMoveSection, notifications }) => {
  const getSectionBadge = (sectionId) => {
    if (!notifications) return 0;
    switch (sectionId) {
      case 'finance': return notifications.pending_finance_requests || 0;
      case 'choir': return notifications.pending_choir_applications || 0;
      case 'secretarial': return (notifications.unread_messages || 0) + (notifications.pending_enrollments || 0);
      default: return 0;
    }
  };

  return (
    <div className="lg:hidden">
      <div className="bg-white rounded-2xl p-2 shadow-lg border border-slate-100 overflow-hidden">
        <div 
          className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-blue-500 scrollbar-track-slate-200"
          style={{ scrollbarWidth: 'thin' }}
        >
          {sections.map((section, index) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            const badgeCount = getSectionBadge(section.id);
            
            return (
              <motion.button
                key={section.id}
                onClick={() => !reorderMode && setActiveSection(section.id)}
                draggable={reorderMode && isMainAdmin}
                onDragStart={(e) => e.dataTransfer.setData('text/plain', index.toString())}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                  onMoveSection(fromIndex, index);
                }}
                className={`relative flex items-center gap-2 px-3 py-2.5 rounded-xl font-medium transition-all text-xs whitespace-nowrap min-h-[44px] touch-manipulation ${
                  isActive
                    ? `bg-gradient-to-r ${section.color} text-white shadow-lg`
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                } ${reorderMode ? 'border-2 border-dashed border-blue-300' : ''}`}
                whileTap={{ scale: 0.95 }}
              >
                {reorderMode && <GripVertical className="w-3 h-3 text-blue-400" />}
                <div className="relative">
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {badgeCount > 0 && (
                    <span className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[8px] font-bold">
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                  )}
                </div>
                <span>{section.label}</span>
              </motion.button>
            );
          })}
        </div>
        <div className="flex justify-center pt-1">
          <span className="text-[10px] text-slate-400">← Scroll for more →</span>
        </div>
      </div>
    </div>
  );
};

// Notification Stats Card with live count
const NotificationStatCard = ({ icon: Icon, label, value, color, onClick, isLoading }) => (
  <motion.button
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    onClick={onClick}
    className={`bg-gradient-to-br ${color} rounded-xl p-4 text-white shadow-lg text-left w-full hover:shadow-xl transition-shadow`}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className="flex items-center justify-between mb-2">
      <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
        <Icon className="w-5 h-5" />
      </div>
      {isLoading && (
        <RefreshCw className="w-4 h-4 animate-spin text-white/70" />
      )}
    </div>
    <p className="text-2xl font-bold">{isLoading ? '...' : value}</p>
    <p className="text-white/70 text-sm">{label}</p>
  </motion.button>
);

function AdminPanel({ 
  isAuthenticated, 
  memberId, 
  church, 
  setChurch, 
  adminUser, 
  serviceSchedules, 
  setServiceSchedules 
}) {
  const [activeSection, setActiveSection] = useState("analytics");
  const [saveStatus, setSaveStatus] = useState("");
  const [sectionOrder, setSectionOrder] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [notifications, setNotifications] = useState(null);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  const isMainAdmin = adminUser?.role === 'main_admin';
  
  // Fetch notification counts
  const fetchNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const res = await axios.get(`${API}/admin/notifications`);
      setNotifications(res.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);
  
  // Load section order and notifications on mount
  useEffect(() => {
    const loadSectionOrder = async () => {
      try {
        const res = await axios.get(`${API}/admin/section-order`);
        if (res.data.section_order?.length > 0) {
          setSectionOrder(res.data.section_order);
        }
      } catch (error) {
        console.error('Error loading section order:', error);
      }
    };
    loadSectionOrder();
    fetchNotifications();
    
    // Refresh notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);
  
  // Permission check helper - uses RBAC utilities
  const hasPermission = (permission) => {
    if (!adminUser) return true; // Fallback for legacy auth
    if (permission === 'any') return true; // Anyone can access
    
    // Use the RBAC utility for department-based permissions
    if (DEPARTMENT_PERMISSIONS[permission]) {
      return canAccessDepartment(adminUser, permission);
    }
    
    // Check specific permission
    return checkPermission(adminUser, permission);
  };
  
  const handleSaveSettings = async () => {
    setSaveStatus("saving");
    try {
      await axios.post(`${API}/church`, {
        name: church.name,
        address: church.address,
        verse_of_day: church.verseOfDay,
        giving_url: church.givingUrl,
        service_times: church.serviceTimes
      });
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(""), 2000);
    } catch (error) {
      console.error("Error saving settings:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(""), 3000);
    }
  };
  
  // Define admin sections with permissions (ordered as per admin screenshot)
  const adminSections = [
    { id: "choir", label: "Choir Ministry", icon: Music, color: "from-purple-500 to-violet-500", permission: "choir" },
    { id: "finance", label: "Finance", icon: PoundSterling, color: "from-emerald-500 to-green-500", permission: "finance" },
    { id: "shepherd", label: "Shepherd's Corner", icon: MessageCircle, color: "from-purple-500 to-indigo-500", permission: "shepherd" },
    { id: "analytics", label: "Analytics", icon: Activity, color: "from-blue-500 to-indigo-500", permission: "analytics" },
    { id: "rota", label: "Church Rota", icon: Calendar, color: "from-blue-600 to-cyan-500", permission: "rota" },
    { id: "announcements", label: "Announcements", icon: Bell, color: "from-red-500 to-pink-500", permission: "announcements" },
    { id: "rules", label: "Rules & Tenets", icon: BookOpen, color: "from-amber-500 to-orange-500", permission: "rules" },
    { id: "projects", label: "Church Projects", icon: Wheat, color: "from-amber-500 to-yellow-500", permission: "projects" },
    { id: "usher", label: "Usher", icon: DoorOpen, color: "from-orange-500 to-amber-500", permission: "usher" },
    { id: "sundayschool", label: "Sunday School", icon: GraduationCap, color: "from-green-500 to-emerald-500", permission: "sundayschool" },
    { id: "media", label: "Media", icon: MonitorPlay, color: "from-red-500 to-rose-500", permission: "media" },
    { id: "biblelessons", label: "Bible Lessons", icon: BookOpen, color: "from-indigo-500 to-purple-500", permission: "biblelessons" },
    { id: "secretarial", label: "Secretarial & Admin", icon: Building2, color: "from-slate-600 to-slate-700", permission: "secretary" },
    { id: "volunteer", label: "Volunteer Attendance", icon: UserCheck, color: "from-amber-500 to-orange-500", permission: "volunteer" }
  ];
  
  // Filter sections based on permissions and apply saved order
  const getOrderedSections = () => {
    let filtered = adminSections.filter(section => hasPermission(section.permission));
    if (sectionOrder.length > 0) {
      const ordered = [];
      sectionOrder.forEach(id => {
        const section = filtered.find(s => s.id === id);
        if (section) ordered.push(section);
      });
      // Add any new sections not in saved order
      filtered.forEach(s => {
        if (!ordered.find(o => o.id === s.id)) ordered.push(s);
      });
      return ordered;
    }
    return filtered;
  };
  
  const visibleSections = getOrderedSections();
  
  // Handle section reordering
  const moveSection = (fromIndex, toIndex) => {
    const newOrder = [...visibleSections];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    setSectionOrder(newOrder.map(s => s.id));
  };
  
  const saveSectionOrder = async () => {
    try {
      await axios.put(`${API}/admin/section-order`, { 
        section_order: sectionOrder.length > 0 ? sectionOrder : visibleSections.map(s => s.id) 
      });
      setReorderMode(false);
    } catch (error) {
      console.error('Error saving section order:', error);
    }
  };

  // Get current time greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  
  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Professional Admin Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl lg:rounded-3xl bg-gradient-to-br from-violet-950 via-purple-900 to-violet-950 p-6 lg:p-8 text-white shadow-2xl border border-amber-400/20"
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-500/20 rounded-full blur-3xl translate-y-32 -translate-x-32"></div>
        <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-amber-400/10 rounded-full blur-2xl"></div>
        
        <div className="relative z-10">
          {/* Top row */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <motion.div 
                className="p-3 lg:p-4 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 shadow-lg shadow-amber-500/30"
                whileHover={{ rotate: 5, scale: 1.05 }}
              >
                <LayoutDashboard className="w-6 h-6 lg:w-8 lg:h-8 text-violet-950" />
              </motion.div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold">{getGreeting()}, {adminUser?.full_name?.split(' ')[0] || 'Admin'}</h1>
                <p className="text-amber-200/80 text-sm">
                  {isMainAdmin ? 'Main Administrator' : 'Department Admin'} • Dashboard
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 lg:gap-3">
              {/* Search (desktop only) */}
              <div className="hidden lg:flex items-center bg-slate-800/50 rounded-xl px-4 py-2 border border-slate-700/50">
                <Search className="w-4 h-4 text-slate-400 mr-2" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="bg-transparent text-sm text-white placeholder-slate-400 outline-none w-40"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {/* Reorder button (main admin only) */}
              {isMainAdmin && (
                <motion.button
                  onClick={() => reorderMode ? saveSectionOrder() : setReorderMode(true)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${
                    reorderMode 
                      ? 'bg-green-500/20 border border-green-500/30 text-green-400 hover:bg-green-500/30' 
                      : 'bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:bg-slate-700/50'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {reorderMode ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span className="hidden sm:inline">Save Order</span>
                    </>
                  ) : (
                    <>
                      <GripVertical className="w-4 h-4" />
                      <span className="hidden sm:inline">Reorder</span>
                    </>
                  )}
                </motion.button>
              )}
              
              {/* Status indicator */}
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <motion.div
                  className="w-2 h-2 rounded-full bg-emerald-400"
                  animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
                <span className="text-emerald-400 text-sm font-medium">System Online</span>
              </div>
            </div>
          </div>
          
          {/* Quick Stats Row (visible on larger screens) */}
          <div className="hidden lg:grid grid-cols-4 gap-4">
            <NotificationStatCard 
              icon={Mail} 
              label="Unread Messages" 
              value={notifications?.unread_messages || 0}
              color="from-blue-600 to-blue-700"
              onClick={() => setActiveSection('secretarial')}
              isLoading={loadingNotifications}
            />
            <NotificationStatCard 
              icon={PoundSterling} 
              label="Finance Requests" 
              value={notifications?.pending_finance_requests || 0}
              color="from-amber-500 to-orange-600"
              onClick={() => setActiveSection('finance')}
              isLoading={loadingNotifications}
            />
            <NotificationStatCard 
              icon={Users} 
              label="New Members (7d)" 
              value={notifications?.new_members || 0}
              color="from-purple-600 to-purple-700"
              onClick={() => setActiveSection('secretarial')}
              isLoading={loadingNotifications}
            />
            <NotificationStatCard 
              icon={Music} 
              label="Choir Applications" 
              value={notifications?.pending_choir_applications || 0}
              color="from-emerald-600 to-green-600"
              onClick={() => setActiveSection('choir')}
              isLoading={loadingNotifications}
            />
          </div>
          
          {/* Total Notifications Badge */}
          {notifications?.total > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center justify-center gap-2 mt-4 p-3 rounded-xl bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30"
            >
              <Bell className="w-5 h-5 text-red-400" />
              <span className="text-red-300 font-medium">
                You have {notifications.total} item{notifications.total !== 1 ? 's' : ''} requiring attention
              </span>
              <motion.button
                onClick={fetchNotifications}
                className="ml-2 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.3 }}
              >
                <RefreshCw className={`w-4 h-4 text-white ${loadingNotifications ? 'animate-spin' : ''}`} />
              </motion.button>
            </motion.div>
          )}
        </div>
      </motion.div>
      
      {/* Navigation Layout */}
      <div className="flex gap-6">
        {/* Desktop Sidebar */}
        <AdminSidebar 
          sections={visibleSections}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          reorderMode={reorderMode}
          isMainAdmin={isMainAdmin}
          onMoveSection={moveSection}
          notifications={notifications}
        />
        
        {/* Main Content Area */}
        <div className="flex-1 min-w-0">
          {/* Mobile Navigation */}
          <MobileNavTabs 
            sections={visibleSections}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            reorderMode={reorderMode}
            isMainAdmin={isMainAdmin}
            onMoveSection={moveSection}
            notifications={notifications}
          />
          
          {/* Permission Warning */}
          {!hasPermission(activeSection) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3"
            >
              <div className="p-2 rounded-xl bg-amber-100">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <p className="text-amber-800 font-medium">You don't have permission to access this section.</p>
            </motion.div>
          )}
          
          {/* Admin Content */}
          <div className="mt-4 lg:mt-0">
            <AnimatePresence mode="wait">
              {/* Analytics Dashboard */}
              {activeSection === "analytics" && (
                <motion.div 
                  key="analytics" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <AnalyticsDashboard adminUser={adminUser} onNavigateToSection={setActiveSection} />
                </motion.div>
              )}
              
              {/* Usher Department Section */}
              {activeSection === "usher" && (
                <motion.div 
                  key="usher" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <UsherDepartmentSection 
                    adminUser={adminUser}
                    renderAttendance={() => <GPSAttendancePage isAuthenticated={isAuthenticated} memberId={memberId} />}
                    renderManualEntry={() => <ManualAttendanceEntry />}
                  />
                </motion.div>
              )}
              
              {/* Sunday School Department Section */}
              {activeSection === "sundayschool" && (
                <motion.div 
                  key="sundayschool" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <SundaySchoolDepartmentSection adminUser={adminUser} />
                </motion.div>
              )}
              
              {/* Church Projects Section */}
              {activeSection === "projects" && (
                <motion.div 
                  key="projects" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <HarvestSection adminUser={adminUser} />
                </motion.div>
              )}
              
              {/* Choir Ministry Section */}
              {activeSection === "choir" && (
                <motion.div 
                  key="choir" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChoirMinistrySection adminUser={adminUser} />
                </motion.div>
              )}
              
              {/* Finance Section */}
              {activeSection === "finance" && (
                <motion.div 
                  key="finance" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <FinanceSection adminUser={adminUser} />
                </motion.div>
              )}
              
              {/* Shepherd's Corner Section */}
              {activeSection === "shepherd" && (
                <motion.div 
                  key="shepherd" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <ShepherdCornerSection adminUser={adminUser} />
                </motion.div>
              )}
              
              {/* Media Department Section */}
              {activeSection === "media" && (
                <motion.div 
                  key="media" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <MediaDepartmentSection adminUser={adminUser} />
                </motion.div>
              )}
              
              {/* Bible Lesson Management Section */}
              {activeSection === "biblelessons" && (
                <motion.div 
                  key="biblelessons" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <BibleLessonManagement />
                </motion.div>
              )}
              
              {/* Secretarial & Admin Department Section */}
              {activeSection === "secretarial" && (
                <motion.div 
                  key="secretarial" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <SecretarialAdminSection 
                    adminUser={adminUser}
                    serviceSchedules={serviceSchedules}
                    setServiceSchedules={setServiceSchedules}
                    renderServiceSchedule={() => <ServiceScheduleSection schedules={serviceSchedules} setSchedules={setServiceSchedules} />}
                    renderMembers={() => <MembersSection onNavigateToAttendance={() => setActiveSection("attendance")} isMainAdmin={isMainAdmin} />}
                    renderSettings={() => (
                      <SettingsSection 
                        church={church} 
                        setChurch={setChurch} 
                        saveStatus={saveStatus}
                        onSaveSettings={handleSaveSettings}
                      />
                    )}
                  />
                </motion.div>
              )}
              
              {/* Church Rota Management Section */}
              {activeSection === "rota" && (
                <motion.div 
                  key="rota" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <RotaManagementModule adminUser={adminUser} />
                </motion.div>
              )}
              
              {/* Announcements Section */}
              {activeSection === "announcements" && (
                <motion.div 
                  key="announcements" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <AnnouncementsModule adminUser={adminUser} />
                </motion.div>
              )}
              
              {/* Rules & Tenets Section */}
              {activeSection === "rules" && (
                <motion.div 
                  key="rules" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-8"
                >
                  <RulesTenantsModule adminUser={adminUser} />
                  <div className="border-t border-slate-200 pt-8">
                    <BibleQuotesModule />
                  </div>
                </motion.div>
              )}

              {/* Volunteer Attendance & Reports Section */}
              {activeSection === "volunteer" && (
                <motion.div 
                  key="volunteer" 
                  initial={{ opacity: 0, x: 20 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <VolunteerSection />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
